import React, {useEffect} from 'react';
import {View, Text, ScrollView, StyleSheet} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useAppSelector} from '../store';
import {useAppNavigation} from '../hooks/useAppNavigation';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {
  DarkScreen,
  GlassPanel,
  Heading,
  BodyText,
  Button,
  theme,
} from '../components/DarkTheme';

export default function AccountDeleteScreen() {
  const {t} = useTranslation();
  const navigation = useAppNavigation();
  const {isAuthenticated} = useAppSelector(state => state.auth);

  // If already logged in, redirect to account settings where delete button exists
  useEffect(() => {
    if (isAuthenticated) {
      navigation.navigate('AccountSettings');
    }
  }, [isAuthenticated, navigation]);

  if (isAuthenticated) {
    return null;
  }

  return (
    <DarkScreen>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.container}>
          <GlassPanel style={styles.card}>
            <Text style={styles.icon}>delete_forever</Text>
            <Heading style={styles.title}>{t('account.deleteWebTitle')}</Heading>

            <BodyText style={styles.sectionLabel}>
              {t('account.deleteWebDescription')}
            </BodyText>
            <View style={styles.steps}>
              <StepItem number="1" text={t('account.deleteWebStep1')} />
              <StepItem number="2" text={t('account.deleteWebStep2')} />
              <StepItem number="3" text={t('account.deleteWebStep3')} />
              <StepItem number="4" text={t('account.deleteWebStep4')} />
            </View>

            <View style={styles.divider} />

            <BodyText style={styles.sectionTitle}>
              {t('account.deleteWebDataTitle')}
            </BodyText>
            <BodyText style={styles.sectionLabel}>
              {t('account.deleteWebDataDeleted')}
            </BodyText>
            <View style={styles.dataList}>
              <DataItem text={t('account.deleteWebDataList1')} />
              <DataItem text={t('account.deleteWebDataList2')} />
              <DataItem text={t('account.deleteWebDataList3')} />
              <DataItem text={t('account.deleteWebDataList4')} />
            </View>

            <GlassPanel style={styles.retainedBox}>
              <Text style={styles.infoIcon}>info</Text>
              <BodyText style={styles.retainedText}>
                {t('account.deleteWebDataRetained')}
              </BodyText>
            </GlassPanel>

            <Button
              label={t('account.deleteWebLogin')}
              onPress={() => navigation.navigate('Home')}
            />
          </GlassPanel>
        </View>
      </ScrollView>
    </DarkScreen>
  );
}

function StepItem({number, text}: {number: string; text: string}) {
  return (
    <View style={styles.stepItem}>
      <View style={styles.stepNumber}>
        <Text style={styles.stepNumberText}>{number}</Text>
      </View>
      <BodyText style={styles.stepText}>{text}</BodyText>
    </View>
  );
}

function DataItem({text}: {text: string}) {
  return (
    <View style={styles.dataItem}>
      <Text style={styles.bullet}>close</Text>
      <BodyText style={styles.dataText}>{text}</BodyText>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  card: {
    maxWidth: 520,
    width: '100%',
    padding: 32,
  },
  icon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 48,
    color: theme.error,
    textAlign: 'center',
    marginBottom: 8,
  },
  title: {
    textAlign: 'center',
    marginBottom: 24,
  },
  sectionLabel: {
    color: theme.textMuted,
    lineHeight: 22,
    marginBottom: 12,
  },
  sectionTitle: {
    color: theme.text,
    fontWeight: '600',
    fontSize: 16,
    marginBottom: 8,
  },
  steps: {
    gap: 12,
    marginBottom: 24,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: theme.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  stepNumberText: {
    color: theme.primary,
    fontWeight: '700',
    fontSize: 14,
  },
  stepText: {
    color: theme.text,
    flex: 1,
    lineHeight: 22,
    paddingTop: 3,
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    marginBottom: 24,
  },
  dataList: {
    gap: 8,
    marginBottom: 16,
  },
  dataItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  bullet: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 18,
    color: theme.error,
    marginTop: 1,
  },
  dataText: {
    color: theme.textMuted,
    flex: 1,
    lineHeight: 22,
  },
  retainedBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 14,
    marginBottom: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  infoIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.primary,
    marginTop: 1,
  },
  retainedText: {
    color: theme.textMuted,
    flex: 1,
    fontSize: 13,
    lineHeight: 20,
  },
});
